import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from "axios"
import type { ApiResponse, PaginatedData } from "@/shared/schemas/api-schema"
import { createDomainAuthUtils } from "@shared/lib/auth-utils"

// Default to admin domain for backward compatibility
const defaultAuthUtils = createDomainAuthUtils('admin')

export class ApiError extends Error {
  constructor(public response: ApiResponse<unknown>) {
    super(response.message || "API error occurred")
    this.name = "ApiError"
  }
}

interface SuccessResponse<T> {
  success: boolean
  message?: string
  data?: T
}

interface ApiConfig {
  baseUrl: string
  headers?: Record<string, string>
  timeout?: number
  enableLogging?: boolean
  domain?: DomainName
}

interface RequestOptions {
  headers?: Record<string, string>
  timeout?: number
  signal?: AbortSignal
}

// Domain types and handlers
export type DomainName = 'admin' | 'fingerprinting' | 'immigration'

const getBaseUrlForDomain = (domain?: DomainName): string => {
  const adminBaseUrl = import.meta.env.VITE_API_BASE_URL || "/api"
  const intakeBaseUrl = import.meta.env.VITE_INTAKE_API_BASE_URL || adminBaseUrl
  
  switch (domain) {
    case 'fingerprinting':
    case 'immigration':
      return intakeBaseUrl
    case 'admin':
    default:
      return adminBaseUrl
  }
}

const defaultConfig: ApiConfig = {
  baseUrl: import.meta.env.VITE_API_BASE_URL || "/api",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  enableLogging: import.meta.env.DEV,
}

type RequestInterceptor = (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig
type ResponseInterceptor = (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>
type ErrorInterceptor = (error: any) => any

type AuthErrorHandler = () => void

const domainAuthErrorHandlers: Map<DomainName, AuthErrorHandler> = new Map()
const domainDobConfirmationHandlers: Map<DomainName, AuthErrorHandler> = new Map()

let onAuthError: (() => void) | null = null

export const setAuthErrorHandler = (handler: () => void) => {
  onAuthError = handler
}

export const setDomainAuthErrorHandler = (domain: DomainName, handler: AuthErrorHandler): void => {
  domainAuthErrorHandlers.set(domain, handler)
}

export const setDomainDobConfirmationHandler = (domain: DomainName, handler: AuthErrorHandler): void => {
  domainDobConfirmationHandlers.set(domain, handler)
}

const getDomainDobConfirmationHandler = (domain: DomainName): AuthErrorHandler | undefined => {
  return domainDobConfirmationHandlers.get(domain)
}

export const getDomainFromPath = (path: string): DomainName => {
  const normalizedPath = path.toLowerCase()
  if (normalizedPath.includes('/fingerprinting') || normalizedPath.includes('/intake')) {
    return 'fingerprinting'
  }
  if (normalizedPath.includes('/immigration')) {
    return 'immigration'
  }
  return 'admin'
}

const getDomainAuthErrorHandler = (domain: DomainName): AuthErrorHandler | undefined => {
  return domainAuthErrorHandlers.get(domain)
}

export class ApiFactory {
  private axiosInstance: AxiosInstance
  private config: ApiConfig
  private isRefreshing = false
  private failedQueue: Array<{
    resolve: (value?: any) => void
    reject: (reason?: any) => void
  }> = []
  private instanceAuthErrorHandler: (() => void) | null = null
  private domainAuthUtils: ReturnType<typeof createDomainAuthUtils>

  constructor(config: Partial<ApiConfig> = {}) {
    this.config = { ...defaultConfig, ...config }
    this.domainAuthUtils = createDomainAuthUtils(this.config.domain || 'admin')
    
    this.axiosInstance = axios.create({
      baseURL: this.config.baseUrl,
      timeout: this.config.timeout,
      headers: this.config.headers,
    })

    // Set up default interceptors
    this.setupDefaultInterceptors()
  }

  setInstanceAuthErrorHandler(handler: () => void): this {
    this.instanceAuthErrorHandler = handler
    return this
  }

  // Process failed queue after token refresh
  private processQueue(error: any, token: string | null = null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error)
      } else {
        resolve(token)
      }
    })
    
    this.failedQueue = []
  }

  private handleAuthError(requestPath?: string): void {
    const domain = this.config.domain || (requestPath ? getDomainFromPath(requestPath) : 'admin')
    this.domainAuthUtils.clearAuth()
    
    const domainHandler = getDomainAuthErrorHandler(domain)
    
    if (this.instanceAuthErrorHandler) {
      this.instanceAuthErrorHandler()
    } else if (domainHandler) {
      domainHandler()
    } else if (onAuthError) {
      onAuthError()
    } else {
      if (typeof window !== 'undefined') {
        window.location.href = '/auth/login'
      }
    }
  }

  private setupDefaultInterceptors(): void {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const skipAuth = Boolean(config.headers && (config.headers as any)['X-Skip-Auth'])
        if (skipAuth && config.headers) {
          delete (config.headers as any)['X-Skip-Auth']
        }

        const token = this.domainAuthUtils.getAccessToken()
        if (!skipAuth && token && config.headers) {
          if (!this.domainAuthUtils.isTokenValid(token)) {
            this.handleAuthError(config.url)
            return Promise.reject(new Error('Token expired'))
          }
          config.headers.Authorization = `Bearer ${token}`
        }

        if (this.config.enableLogging) {
          console.log(`🚀 [${config.method?.toUpperCase()}] ${config.url}`, {
            headers: config.headers,
            params: config.params,
            data: config.data,
          })
        }

        return config
      },
      (error) => {
        if (this.config.enableLogging) {
          console.error("❌ Request Error:", error)
        }
        return Promise.reject(error)
      }
    )

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        if (this.config.enableLogging) {
          console.log(`? [${response.status}] ${response.config.url}`, response.data)
        }

        return response
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
        const requestUrl = (error.config?.url || '').toString().toLowerCase()
        const isAuthEndpoint = requestUrl.includes('auth/login') ||
                               requestUrl.includes('auth/register') ||
                               requestUrl.includes('auth/refresh') ||
                               requestUrl.includes('auth/logout') ||
                               requestUrl.includes('auth/forgot-password') ||
                               requestUrl.includes('auth/reset-password') ||
                               requestUrl.includes('auth/change-password') ||
                               requestUrl.includes('intake/auth/login') ||
                               requestUrl.includes('intake/auth/change-password') ||
                               requestUrl.includes('intake/auth/forgot-password') ||
                               requestUrl.includes('intake/auth/reset-password') ||
                               requestUrl.includes('vietcan/auth/forgot-password') ||
                               requestUrl.includes('vietcan/auth/reset-password')

        // Log error in development mode
        if (this.config.enableLogging) {
          console.error(`? [${error.response?.status || 'Network'}] ${error.config?.url}`, error.response?.data)
        }

        // For auth endpoints (login/register/etc), do not trigger global auth handlers on 401.
        // Let callers handle the error so the page doesn't hard-refresh on invalid credentials.
        if (error.response?.status === 401 && isAuthEndpoint) {
          return Promise.reject(error)
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // Queue the request while refresh is in progress
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject })
            }).then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              return this.axiosInstance(originalRequest)
            }).catch((err) => {
              return Promise.reject(err)
            })
          }

          originalRequest._retry = true
          this.isRefreshing = true

          try {
            const refreshToken = defaultAuthUtils.getRefreshToken()
            if (refreshToken) {
              this.handleAuthError(originalRequest.url)
              this.processQueue(error, null)
            } else {
              this.processQueue(error, null)
              this.handleAuthError(originalRequest.url)
            }
          } catch (refreshError) {
            this.processQueue(refreshError, null)
            this.handleAuthError(originalRequest.url)
          } finally {
            this.isRefreshing = false
          }
          
          return Promise.reject(error)
        }

        if (error.response) {
          const response = error.response
          const errorData = response.data as any
          const apiResponse: ApiResponse<unknown> = {
            statusCode: response.status,
            message: errorData?.message || error.message || "An error occurred",
            data: undefined,
            errorCode: errorData?.errorCode || "HTTP_ERROR",
          }

          if (response.status === 401) {
            // For non-auth endpoints, trigger global auth handling
            apiResponse.message = errorData?.message || "Authentication required"
            apiResponse.errorCode = errorData?.errorCode || "UNAUTHORIZED"
            this.handleAuthError(requestUrl)
          } else if (response.status === 403) {
            const isDobConfirmationRequired =
              errorData?.code === 'DOB_CONFIRMATION_REQUIRED' ||
              errorData?.errorCode === 'DOB_CONFIRMATION_REQUIRED'
            const domain = this.config.domain || (requestUrl ? getDomainFromPath(requestUrl) : 'admin')
            const isIntakeDomain = domain === 'fingerprinting' || requestUrl.includes('intake/')

            if (isDobConfirmationRequired && isIntakeDomain) {
              const dobHandler = getDomainDobConfirmationHandler(domain)
              if (dobHandler) {
                dobHandler()
              }
            }

            apiResponse.message = errorData?.message || "Access forbidden - insufficient permissions"
            apiResponse.errorCode = "FORBIDDEN"
            ;(apiResponse as any).details = errorData?.details
            ;(apiResponse as any).suggestion = errorData?.suggestion
            apiResponse.retryable = false
          } else if (response.status === 404) {
            apiResponse.message = "Resource not found"
            apiResponse.errorCode = "NOT_FOUND"
          } else if (response.status >= 500) {
            apiResponse.message = "Server error - please try again later"
            apiResponse.errorCode = "SERVER_ERROR"
          }

          throw new ApiError(apiResponse)
        } else if (error.request) {
          // Network error or timeout
          const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout')
          throw new ApiError({
            statusCode: 0,
            message: isTimeout 
              ? "Request timed out - the operation may still be in progress on the server"
              : "Network error - please check your connection",
            errorCode: isTimeout ? "TIMEOUT_ERROR" : "NETWORK_ERROR",
          })
        } else {
          // Other error
          throw new ApiError({
            statusCode: 500,
            message: error.message || "An unexpected error occurred",
            errorCode: "UNKNOWN_ERROR",
          })
        }
      }
    )
  }

  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.axiosInstance.interceptors.request.use(interceptor)
  }

  addResponseInterceptor(onFulfilled?: ResponseInterceptor, onRejected?: ErrorInterceptor): void {
    this.axiosInstance.interceptors.response.use(onFulfilled, onRejected)
  }

  private mergeHeaders(customHeaders?: Record<string, string>): Record<string, string> {
    return {
      ...this.config.headers,
      ...customHeaders,
    }
  }

  setHeaders(headers: Record<string, string>): this {
    Object.assign(this.axiosInstance.defaults.headers.common, headers)
    return this
  }

  removeHeaders(headerNames: string[]): this {
    headerNames.forEach(name => {
      delete this.axiosInstance.defaults.headers.common[name]
    })
    return this
  }

  clearCustomHeaders(): this {
    this.axiosInstance.defaults.headers.common = { ...this.config.headers }
    return this
  }

  async get<T>(
    endpoint: string, 
    params?: Record<string, unknown>, 
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    try {
      const config = {
        params,
        headers: options?.headers ? this.mergeHeaders(options.headers) : undefined,
        timeout: options?.timeout,
        signal: options?.signal,
      }

      const response = await this.axiosInstance.get<T | ApiResponse<T> | SuccessResponse<T>>(endpoint, config)
      
      // Check if response is already in ApiResponse format (with statusCode)
      if (response.data && typeof response.data === 'object' && 'statusCode' in response.data) {
        return response.data as ApiResponse<T>
      }
      
      // Check if response is in success format (with success boolean)
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const successResponse = response.data as SuccessResponse<T>
        return {
          statusCode: response.status,
          message: successResponse.message || "Success",
          data: successResponse.data,
          errorCode: successResponse.success ? null : "REQUEST_FAILED",
        }
      }
      
      // If neither format, wrap the raw data in ApiResponse format
      return {
        statusCode: response.status,
        message: "Success",
        data: response.data as T,
        errorCode: null,
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.data) {
          const errorData = axiosError.response.data
          
          if (errorData && typeof errorData === 'object' && 'statusCode' in errorData) {
            throw new ApiError({
              statusCode: errorData.statusCode,
              message: errorData.message || "An error occurred",
              errorCode: errorData.errorCode || "HTTP_ERROR",
            })
          }
        }
      }
      
      throw new ApiError({
        statusCode: 500,
        message: "An unexpected error occurred",
        errorCode: "UNKNOWN_ERROR",
      })
    }
  }

  async post<T>(
    endpoint: string, 
    data?: unknown, 
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    try {
      const config = {
        headers: options?.headers ? this.mergeHeaders(options.headers) : undefined,
        timeout: options?.timeout,
        signal: options?.signal,
      }

      const response = await this.axiosInstance.post<T | ApiResponse<T> | SuccessResponse<T>>(endpoint, data, config)
      
      if (response.data && typeof response.data === 'object' && 'statusCode' in response.data) {
        return response.data as ApiResponse<T>
      }
      
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const successResponse = response.data as SuccessResponse<T>
        return {
          statusCode: response.status,
          message: successResponse.message || "Success",
          data: successResponse.data,
          errorCode: successResponse.success ? null : "REQUEST_FAILED",
        }
      }
      
      // If neither format, wrap the raw data in ApiResponse format
      return {
        statusCode: response.status,
        message: "Success",
        data: response.data as T,
        errorCode: null,
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.data) {
          const errorData = axiosError.response.data
          
          if (errorData && typeof errorData === 'object' && 'statusCode' in errorData) {
            throw new ApiError({
              statusCode: errorData.statusCode,
              message: errorData.message || "An error occurred",
              errorCode: errorData.errorCode || "HTTP_ERROR",
            })
          }
        }
      }
      
      throw new ApiError({
        statusCode: 500,
        message: "An unexpected error occurred",
        errorCode: "UNKNOWN_ERROR",
      })
    }
  }

  // PUT method with custom headers support
  async put<T>(
    endpoint: string, 
    data?: unknown, 
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    try {
      const config = {
        headers: options?.headers ? this.mergeHeaders(options.headers) : undefined,
        timeout: options?.timeout,
        signal: options?.signal,
      }

      const response = await this.axiosInstance.put<T | ApiResponse<T> | SuccessResponse<T>>(endpoint, data, config)
      
      // Check if response is already in ApiResponse format (with statusCode)
      if (response.data && typeof response.data === 'object' && 'statusCode' in response.data) {
        return response.data as ApiResponse<T>
      }
      
      // Check if response is in success format (with success boolean)
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const successResponse = response.data as SuccessResponse<T>
        return {
          statusCode: response.status,
          message: successResponse.message || "Success",
          data: successResponse.data,
          errorCode: successResponse.success ? null : "REQUEST_FAILED",
        }
      }
      
      // If neither format, wrap the raw data in ApiResponse format
      return {
        statusCode: response.status,
        message: "Success",
        data: response.data as T,
        errorCode: null,
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      
      // Handle axios errors with response data
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.data) {
          const errorData = axiosError.response.data
          
          // If the error response has the expected format, use it
          if (errorData && typeof errorData === 'object' && 'statusCode' in errorData) {
            throw new ApiError({
              statusCode: errorData.statusCode,
              message: errorData.message || "An error occurred",
              errorCode: errorData.errorCode || "HTTP_ERROR",
            })
          }
        }
      }
      
      throw new ApiError({
        statusCode: 500,
        message: "An unexpected error occurred",
        errorCode: "UNKNOWN_ERROR",
      })
    }
  }

  // PATCH method with custom headers support
  async patch<T>(
    endpoint: string, 
    data?: unknown, 
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    try {
      const config = {
        headers: options?.headers ? this.mergeHeaders(options.headers) : undefined,
        timeout: options?.timeout,
        signal: options?.signal,
      }

      const response = await this.axiosInstance.patch<T | ApiResponse<T> | SuccessResponse<T>>(endpoint, data, config)
      
      // Check if response is already in ApiResponse format (with statusCode)
      if (response.data && typeof response.data === 'object' && 'statusCode' in response.data) {
        return response.data as ApiResponse<T>
      }
      
      // Check if response is in success format (with success boolean)
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const successResponse = response.data as SuccessResponse<T>
        return {
          statusCode: response.status,
          message: successResponse.message || "Success",
          data: successResponse.data,
          errorCode: successResponse.success ? null : "REQUEST_FAILED",
        }
      }
      
      // If neither format, wrap the raw data in ApiResponse format
      return {
        statusCode: response.status,
        message: "Success",
        data: response.data as T,
        errorCode: null,
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      
      // Handle axios errors with response data
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.data) {
          const errorData = axiosError.response.data
          
          // If the error response has the expected format, use it
          if (errorData && typeof errorData === 'object' && 'statusCode' in errorData) {
            throw new ApiError({
              statusCode: errorData.statusCode,
              message: errorData.message || "An error occurred",
              errorCode: errorData.errorCode || "HTTP_ERROR",
            })
          }
        }
      }
      
      throw new ApiError({
        statusCode: 500,
        message: "An unexpected error occurred",
        errorCode: "UNKNOWN_ERROR",
      })
    }
  }

  // DELETE method with custom headers support
  async delete<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    try {
      const config = {
        headers: options?.headers ? this.mergeHeaders(options.headers) : undefined,
        timeout: options?.timeout,
        signal: options?.signal,
      }

      const response = await this.axiosInstance.delete<T | ApiResponse<T> | SuccessResponse<T>>(endpoint, config)
      
      // Check if response is already in ApiResponse format (with statusCode)
      if (response.data && typeof response.data === 'object' && 'statusCode' in response.data) {
        return response.data as ApiResponse<T>
      }
      
      // Check if response is in success format (with success boolean)
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const successResponse = response.data as SuccessResponse<T>
        return {
          statusCode: response.status,
          message: successResponse.message || "Success",
          data: successResponse.data,
          errorCode: successResponse.success ? null : "REQUEST_FAILED",
        }
      }
      
      // If neither format, wrap the raw data in ApiResponse format
      return {
        statusCode: response.status,
        message: "Success",
        data: response.data as T,
        errorCode: null,
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      
      // Handle axios errors with response data
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.data) {
          const errorData = axiosError.response.data
          
          // If the error response has the expected format, use it
          if (errorData && typeof errorData === 'object' && 'statusCode' in errorData) {
            throw new ApiError({
              statusCode: errorData.statusCode,
              message: errorData.message || "An error occurred",
              errorCode: errorData.errorCode || "HTTP_ERROR",
            })
          }
        }
      }
      
      throw new ApiError({
        statusCode: 500,
        message: "An unexpected error occurred",
        errorCode: "UNKNOWN_ERROR",
      })
    }
  }

  // Specialized method for paginated requests with custom headers support
  async getPaginated<T>(
    endpoint: string, 
    params: Record<string, any>, 
    options?: RequestOptions
  ): Promise<PaginatedData<T> | undefined> {
    const response = await this.get<PaginatedData<T>>(endpoint, params, options)
    return response.data
  }

  // Retry a failed request with new config (for backward compatibility)
  async retryRequest(config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
    return this.axiosInstance.request(config)
  }

  // Get the underlying axios instance (for advanced usage)
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance
  }
}

// Create and export a default API instance (uses admin base URL)
export const api = new ApiFactory()

// Factory function to create API instances with different configurations
interface ICreateApiOptions extends Partial<ApiConfig> {
  prefixUrl?: string
  domain?: DomainName
}

export function createApi(config: ICreateApiOptions = {}): ApiFactory {
  const { prefixUrl, domain, ...restConfig } = config
  const domainBaseUrl = getBaseUrlForDomain(domain)
  
  return new ApiFactory({
    baseUrl: prefixUrl ? `${domainBaseUrl}/${prefixUrl}` : domainBaseUrl,
    ...(domain ? { domain } : {}),
    ...restConfig,
  })
}

// Export axios instance for direct usage if needed
export const axiosInstance = api.getAxiosInstance()
